import { Icon } from '@octano/global-ui';
import { Controller } from 'react-hook-form';
import { components as ReactSelectComponents } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FormGroup, FormText, Label } from 'reactstrap';

/**
 * Variante "creatable" del `<Select>` form-style de @octano/global-ui.
 *
 * `@octano/global-ui` solo exporta `SelectOutlinedCreateable` (estilo
 * filtro: label uppercase afuera, sin barra verde). Este componente
 * replica la implementación interna del `<Select>` form-style (ver
 * `dist/index.js`) pero usando `react-select/creatable` debajo, para
 * permitir que el usuario escriba un valor nuevo. Reutiliza las clases
 * CSS de la lib (`g-select-base`, `g-select-container`, `g-select-*`,
 * `g-input-border`) que ya están en el bundle SCSS — el look queda
 * pixel-equivalente al `<Select>` estándar de los formularios.
 */
const Control = ({ children, ...rest }: any) => {
  const { hasValue, isDisabled, selectProps } = rest;
  const classHasValue = hasValue ? 'has-value' : 'without-value';
  const classDisabled = isDisabled ? 'disabled' : 'not-disabled';
  return (
    <ReactSelectComponents.Control
      {...rest}
      className={`${classHasValue} ${classDisabled} without-icon`}
    >
      {children}
      <Label htmlFor={selectProps.name} className="g-select-label">
        {selectProps.label?.toUpperCase()}
      </Label>
      <div className="g-input-border" />
    </ReactSelectComponents.Control>
  );
};

const DropdownIndicator = () => (
  <div className="g-select__indicator g-select__arrow-indicator">
    <Icon name="chevron_down" size="14px" />
  </div>
);

const ClearIndicator = ({ innerProps }: any) => (
  <div
    className="g-select__indicator g-select__clear-indicator"
    onMouseDown={innerProps.onMouseDown}
  >
    <Icon name="close" size="12px" />
  </div>
);

interface SelectCreateableProps {
  name: string;
  control: any;
  label: string;
  options: { label: string; value: string | number }[];
  placeholder?: string;
  rules?: any;
  isClearable?: boolean;
  isDisabled?: boolean;
  formatCreateLabel?: (input: string) => string;
}

export const SelectCreateable = ({
  name,
  control,
  rules,
  label,
  options,
  placeholder,
  isClearable = true,
  isDisabled,
  formatCreateLabel = (value) => `Crear "${value}"`,
}: SelectCreateableProps) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
        <FormGroup>
          <div className="g-select-container">
            <CreatableSelect
              {...field}
              id={name}
              label={label as any}
              options={options}
              placeholder={placeholder}
              isClearable={isClearable}
              isDisabled={isDisabled}
              className="g-select-base"
              classNamePrefix="g-select"
              components={{ Control, DropdownIndicator, ClearIndicator }}
              blurInputOnSelect
              formatCreateLabel={formatCreateLabel}
            />
            <FormText className="g-input-error" color="danger">
              {fieldState.error?.message ?? ''}
            </FormText>
          </div>
        </FormGroup>
      )}
    />
  );
};
